import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  fetchAssignment, submitAssignment, transcribeForAssignment, linkAssignmentToChild,
} from '../services/assignmentApi';
import { localScore } from '../utils/textSimilarity';
import { supabase } from '../services/supabaseClient';
import { fetchChildren, loadCurrentChildId, saveCurrentChildId } from '../services/childrenApi';
import { saveRecord } from '../utils/storage';
import { saveAudio } from '../utils/audioStorage';
import { uploadAudio } from '../services/audioStorageApi';
import { saveLog } from '../services/readingLogsApi';
import { rememberCode, rememberName, getLastName } from '../utils/assignmentHistory';
import { createRecorder, getRecordingMimeType } from '../utils/audioRecording';

const GROUP_SIZE_LONG  = 3;
const GROUP_SIZE_SHORT = 2;

const RENSHYU_DAILY_LIMIT = 3;
const HONBAN_DAILY_LIMIT  = 1;

// ⚠️ クライアント側のカウントは「うっかり使いすぎ」を防ぐだけで、
//    不正防止にはならない（localStorageを消せば回避できる）。
//    サーバー側での強制は school-accounts.md の残タスク。
const usageKey = (code, mode) => `ondoku_assign_${code}_${mode}_${new Date().toISOString().slice(0, 10)}`;

function getUsage(code, mode) {
  return Number(localStorage.getItem(usageKey(code, mode)) || 0);
}
function incrementUsage(code, mode) {
  localStorage.setItem(usageKey(code, mode), String(getUsage(code, mode) + 1));
}

/**
 * 配布課題の音読画面。
 *
 * 2通りの入り方がある：
 *  1. 配布リンク（/t/:code）→ ログイン不要。名前を手入力する
 *  2. ログイン中の子がアプリ内から（`childId`/`childName` を渡す）
 *     → 名前入力を飛ばし、記録が自分の「きろく」にも残る
 *
 * 1でログイン中だった場合は課題をその子に紐づけ、次回からアプリ内に出るようにする。
 */
export default function AssignmentView({ code, childId: propChildId, childName: propChildName, onDone, onBack }) {
  // loading | notfound | expired | name | modeSelect | ready | countdown | recording | scoring | result
  const [phase, setPhase] = useState('loading');
  const [assignment, setAssignment] = useState(null);
  // 未ログインでも前回の名前を引き継ぐ（毎回入力させない）
  const [studentName, setStudentName] = useState(propChildName ?? getLastName());
  const [childId, setChildId]         = useState(propChildId ?? null);
  const [children, setChildren]       = useState([]); // 「だれがよむ？」の選択肢
  const [mode, setMode]           = useState(null); // 'renshyu' | 'honban'
  const [countdown, setCountdown] = useState(null);
  const [result, setResult]       = useState(null);
  const [audioUrl, setAudioUrl]   = useState(null);
  const [playing, setPlaying]     = useState(false);
  const [usageTick, setUsageTick] = useState(0); // 回数表示の再描画用
  // 本文プレビューが枠に収まるか。収まるなら中央、あふれるなら先頭（右端）寄せ
  const [previewFits, setPreviewFits] = useState(true);

  const mediaRef    = useRef(null);
  const chunksRef   = useRef([]);
  const textBoxRef  = useRef(null);
  const audioRef    = useRef(null);
  const previewRef  = useRef(null);

  // その子として音読を始める（課題の紐づけもここで行う）
  const startAsChild = useCallback(async (child) => {
    setChildId(child.id);
    setStudentName(child.name);
    saveCurrentChildId(child.id);   // 次回以降は選び直さなくてよい
    rememberCode(code, child.id);   // 配布リンク経由でログイン中だった場合、この子として登録し直す
    await linkAssignmentToChild(child.id, code);
    setPhase('modeSelect');
  }, [code]);

  useEffect(() => {
    if (!code) { setPhase('notfound'); return; }

    (async () => {
      const a = await fetchAssignment(code).catch(() => null);
      if (!a)        { setPhase('notfound'); return; }
      if (a.expired) { setPhase('expired');  return; }
      setAssignment(a);
      // 生徒ページに「せんせいからのかだい」として溜める（子どもごとに分ける）。
      // ここで分からない場合（配布リンクを直接開いた等）は startAsChild 側で再登録する
      rememberCode(code, propChildId ?? null);

      // アプリ内から開いた場合は、渡された子の名前をそのまま使う
      if (propChildName) { setPhase('modeSelect'); return; }

      // 配布リンクから開いた場合：ログイン中なら課題をその子に紐づけて
      // 次回からアプリ内に出るようにする。未ログインなら今まで通り名前入力。
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const currentId = loadCurrentChildId();
          const list      = await fetchChildren();
          const current   = list.find((c) => c.id === currentId);

          if (current) {
            // アプリで選択中の子がいる → その子として進む
            await startAsChild(current);
            return;
          }
          if (list.length === 1) {
            await startAsChild(list[0]);
            return;
          }
          if (list.length > 1) {
            // ⚠️ きょうだいが複数いて選択中の子が不明なときに先頭を使うと、
            //    別の子の名前で先生に提出され、きろくも取り違える。必ず選ばせる。
            setChildren(list);
            setPhase('whoseTurn');
            return;
          }
        }
      } catch (e) {
        // ログイン状態が取れなくても音読はできるべきなので、名前入力に落とす
        console.warn('[assignment] ログイン確認に失敗 → 名前入力へ', e);
      }
      setPhase('name');
    })();
  }, [code, propChildId, propChildName, startAsChild]);

  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const lines = assignment?.body ? assignment.body.split('\n').filter(Boolean) : [];
  const fullText = lines.join('');
  const isShortText = lines.length <= 10;
  // 何を読むのか分からないままモードを選ばせないよう、冒頭だけ見せる
  const preview = lines.slice(0, 3).join('');

  const renshyuLeft = RENSHYU_DAILY_LIMIT - getUsage(code, 'renshyu');
  const honbanLeft  = HONBAN_DAILY_LIMIT  - getUsage(code, 'honban');
  void usageTick; // 回数を再計算させるための依存

  // 短い課題文だと縦書きが数列で終わり、枠の左側が大きく空く。
  // 収まるときだけ中央に寄せる（あふれるときは先頭＝右端から見せたい）。
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    setPreviewFits(el.scrollWidth <= el.clientWidth);
  }, [preview, phase]);

  const scrollText = (dir) => {
    const el = textBoxRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.75, behavior: 'smooth' });
  };

  const chooseMode = (m) => {
    setMode(m);
    setResult(null);
    setPhase('ready');
  };

  const startCountdown = () => {
    // ほんばんだけカウントダウンを入れる（れんしゅうはすぐ始められるように）
    if (mode === 'renshyu') { startRecording(); return; }
    setPhase('countdown');
    let c = 3;
    setCountdown(c);
    const timer = setInterval(() => {
      c--;
      if (c === 0) {
        clearInterval(timer);
        setCountdown(null);
        startRecording();
      } else {
        setCountdown(c);
      }
    }, 1000);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = createRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = handleStop;
      mr.start();
      mediaRef.current = mr;
      setPhase('recording');
    } catch {
      alert('マイクが　つかえません。\nブラウザの　せってい　を　かくにんしてね。');
      setPhase('ready');
    }
  };

  const stopRecording = () => {
    mediaRef.current?.stop();
    mediaRef.current?.stream.getTracks().forEach((t) => t.stop());
    setPhase('scoring');
  };

  const handleStop = async () => {
    try {
      const mimeType = mediaRef.current?.mimeType || getRecordingMimeType() || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: mimeType });
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudioUrl(URL.createObjectURL(blob));
      setPlaying(false);

      const { transcript, ai, audioPath } = await transcribeForAssignment(blob, code, mode, fullText);
      // ほんばんはClaude採点、れんしゅう（とAI採点失敗時）はローカル採点
      const { score, stars, feedback, details } = ai ?? localScore(fullText, transcript);
      setResult({ score, stars, feedback, details, ai: !!ai });

      incrementUsage(code, mode);
      setUsageTick((t) => t + 1);
      // 先生ページの「詳細」で見せるので、文字起こしとコメントも一緒に送る
      await submitAssignment(assignment.id, studentName, mode, score, audioPath, {
        transcript, feedback, details,
      });

      // 「きろく」に残す。ゲスト・未ログインでも端末には残るようにする
      // （saveRecord は localStorage 保存なので childId は無くてよい）
      const record = {
        mode,
        // 生徒ページで「課題の記録だけ」を絞り込むための印。
        // localStorage にはそのまま入る（toRow は列を明示指定しているのでクラウドには送られない）
        assignmentId: code,
        title: assignment.title || 'せんせいのかだい',
        totalStars: stars,
        avgScore: score,
        transcript,
        feedback,
        details: details ?? '',
        date: new Date().toISOString(),
      };
      const saved = saveRecord(record, childId);

      // 録音を端末（IndexedDB）に保存。これが無いと、きろくの▶で再生できない。
      // ⚠️ ローカル音声は無制限・削除しない（CLAUDE.md の絶対ルール）
      saveAudio(saved.id, blob);

      // クラウド同期はログイン中の子だけ（child_id が要るため）
      if (childId) {
        await saveLog(saved, childId);
        uploadAudio(saved.id, blob, childId, mimeType);
      }

      setPhase('result');
    } catch (e) {
      // 技術的な内容はコンソールへ。子どもにはやさしい文言だけ見せる
      console.error('[assignment]', e);
      if (e.status === 410) {
        setPhase('expired');
        return;
      }
      // 429 = 課題ごとの1日上限（サーバー側）に到達
      alert(e.status === 429
        ? 'この課題は　きょう　たくさん　つかわれました。\nまた　あした　ためしてね。'
        : 'うまく　いかなかったよ。\nもういちど　ためしてね。');
      setPhase('ready');
    }
  };

  const handlePlay = () => {
    if (!audioUrl) return;
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
    } else {
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.play();
      audio.onended = () => setPlaying(false);
      setPlaying(true);
    }
  };

  // 生徒ページへ。アプリ内から来た場合はリロードせず画面遷移する
  const goMyPage = onDone ?? (() => { window.location.href = '/s'; });
  // もどる先。URL直打ちで来た場合はアプリの画面が無いので生徒ページに帰す
  const goBack   = onBack ?? goMyPage;

  const starStr = (n) => '⭐'.repeat(Math.max(0, n)) + '☆'.repeat(Math.max(0, 3 - n));

  // ── 読み込み・エラー系 ─────────────────────────
  if (phase === 'loading') {
    return (
      <div className="auth-loading">
        <div className="auth-loading-logo">Ondoku Quest</div>
        <div className="auth-loading-dot" />
      </div>
    );
  }

  if (phase === 'notfound' || phase === 'expired') {
    return (
      <div className="screen assign-screen">
        <h2 className="assign-title">
          {phase === 'expired' ? 'この課題は　きげんぎれです' : 'この課題は　みつかりません'}
        </h2>
        <div className="assign-card">
          <p className="assign-note">
            {phase === 'expired'
              ? 'せんせいに　あたらしいリンクを　もらってね'
              : 'リンクが　まちがっているかも　しれません'}
          </p>
        </div>
      </div>
    );
  }

  // ── だれがよむ？（きょうだいが複数いて選択中の子が不明なとき）──
  if (phase === 'whoseTurn') {
    return (
      <div className="screen assign-screen">
        <h2 className="assign-title">📖 {assignment.title || 'せんせいからの　おんどく'}</h2>
        <p className="assign-sub">だれが　よむ？</p>

        {children.map((c) => (
          <button
            key={c.id}
            className="assign-mode-card"
            onClick={() => startAsChild(c)}
          >
            <span className="assign-mode-icon">{c.avatar || '🐣'}</span>
            <span className="assign-mode-body">
              <span className="assign-mode-name">{c.name}</span>
            </span>
          </button>
        ))}
      </div>
    );
  }

  // ── 名前入力 ─────────────────────────────────
  if (phase === 'name') {
    return (
      <div className="screen assign-screen">
        <h2 className="assign-title">📖 {assignment.title || 'せんせいからの　おんどく'}</h2>
        <div className="assign-card">
          <p className="assign-note">なまえを　いれてね</p>
          <input
            className="assign-name-input"
            type="text"
            value={studentName}
            onChange={(e) => setStudentName(e.target.value)}
            placeholder="なまえ"
          />
          <button
            className="assign-btn-primary"
            disabled={!studentName.trim()}
            onClick={() => {
              rememberName(studentName.trim()); // 次回は入力済みの状態にする
              setPhase('modeSelect');
            }}
          >
            はじめる
          </button>
        </div>
      </div>
    );
  }

  // ── モード選択 ────────────────────────────────
  if (phase === 'modeSelect') {
    const deadline = assignment.expires_at ? new Date(assignment.expires_at) : null;

    return (
      // 構造は本体のモード選択と同じだが、色はティール＝学校モード。
      // おうちモード（紫）と見分けがつかないと、生徒も先生も取り違える。
      <div className="screen assign-v9-screen">
        <div className="assign-v9-header">
          <div className="assign-v9-header-inner">
            {/* 配布リンクから来た場合は goBack が生徒ページ行きになり
                下の「生徒ページ」と同じ動きになるので、ここには出さない */}
            {onBack
              ? <button className="assign-v9-back" onClick={goBack}>← もどる</button>
              : <span style={{ width: 72 }} />}
            <div className="assign-v9-title">どちらで　よむ？</div>
            <span style={{ width: 72 }} />
          </div>
          <div className="assign-v9-ribbon">🏫 せんせいからの　かだい</div>
        </div>

        <div className="assign-v9-body">
          <div className="assign-v9-text-card">
            <div className="assign-v9-text-meta">
              <span className="assign-v9-book-badge">『{assignment.title || 'おんどく'}』</span>
              <span className="assign-v9-para-badge">{lines.length}ぎょう</span>
            </div>
            <div
              className={`assign-v9-text-body${previewFits ? ' fits' : ''}`}
              ref={previewRef}
            >
              {preview}
            </div>
            <div className="assign-v9-text-footer">
              <span>🎒 {studentName}さん</span>
              {deadline && <span>⏳ {deadline.getMonth() + 1}/{deadline.getDate()} まで</span>}
            </div>
          </div>

          <div className="assign-v9-action-btns">
            <button
              className="assign-v9-action-btn assign-v9-btn-renshyu"
              onClick={() => chooseMode('renshyu')}
              disabled={renshyuLeft <= 0}
            >
              <div className="assign-v9-btn-icon">📖</div>
              <div className="assign-v9-btn-title">れんしゅう</div>
              {/* 「何ができるか」＋「あと何回か」の2行。ほんばん側と形をそろえる */}
              <div className="assign-v9-btn-sub">
                {renshyuLeft > 0
                  ? <>なんども　できる<br />きょう　あと{renshyuLeft}かい</>
                  : 'きょうは　おわり'}
              </div>
            </button>

            <button
              className="assign-v9-action-btn assign-v9-btn-honban"
              onClick={() => chooseMode('honban')}
              disabled={honbanLeft <= 0}
            >
              <div className="assign-v9-btn-icon">🎯</div>
              <div className="assign-v9-btn-title">ほんばん</div>
              {/* 1日1回しか出せないことを押す前に伝える（押してから知ると取り返しがつかない） */}
              <div className="assign-v9-btn-sub">
                {honbanLeft > 0
                  ? <>せんせいに　ていしゅつ<br />1にち　1かい</>
                  : 'きょうは　ていしゅつずみ'}
              </div>
            </button>
          </div>

          <button className="assign-v9-student-btn" onClick={goMyPage}>
            🎒 生徒ページ
          </button>
        </div>
      </div>
    );
  }

  // ── 録音・結果 ────────────────────────────────
  const isHonban = mode === 'honban';

  return (
    // 縦書き枠は flex:1 で伸びるので、読み上げ中は画面高さを固定しないと
    // 枠が下に伸び続けて録音ボタンが画面外に出る（ほんばん画面と同じ扱いにする）
    <div className={`screen assign-screen${phase !== 'result' ? ' assign-reading' : ''}`}>
      <h2 className="assign-title">
        {isHonban ? '🎯 ほんばん' : '📖 れんしゅう'}
      </h2>
      <p className="assign-sub">『{assignment.title || 'おんどく'}』／{studentName}さん</p>

      {phase !== 'result' && (
        <div className={`text-box-wrap${isShortText ? ' short-wrap' : ''}`}>
          {!isShortText && (
            <button className="scroll-btn scroll-btn-right" onClick={() => scrollText(-1)}>&#8249;</button>
          )}
          <div className={`text-vertical-full${isShortText ? ' short-text' : ''}`} ref={textBoxRef}>
            {lines.map((line, i) => (
              <div
                key={i}
                className={`vertical-line vl-group-${Math.floor(i / (isShortText ? GROUP_SIZE_SHORT : GROUP_SIZE_LONG)) % 2}`}
              >
                {line}
              </div>
            ))}
          </div>
          {!isShortText && (
            <button className="scroll-btn scroll-btn-left" onClick={() => scrollText(1)}>&#8250;</button>
          )}
        </div>
      )}

      {phase === 'ready' && (
        <div className="assign-action">
          <p className="assign-hint">よんだら　ボタンを　おして　とめてね</p>
          <button className="assign-btn-primary" onClick={startCountdown}>🎤 はじめる！</button>
          <button className="assign-btn-text" onClick={() => setPhase('modeSelect')}>
            ← モードを　えらびなおす
          </button>
        </div>
      )}

      {phase === 'countdown' && (
        <div className="countdown-box">
          <div className="countdown-num">{countdown}</div>
          <p>よういして！</p>
        </div>
      )}

      {phase === 'recording' && (
        <div className="assign-action">
          <div className="recording-indicator">
            <span className="rec-dot" />
            <span>ろくおんちゅう…</span>
          </div>
          <button className="assign-btn-stop" onClick={stopRecording}>⏹ よみおわった！</button>
        </div>
      )}

      {phase === 'scoring' && (
        <div className="loading-box">
          <div className="spinner" />
          <p>さいてんちゅう…　まってね！</p>
        </div>
      )}

      {phase === 'result' && result && (
        <div className="assign-card">
          <div className="assign-score">{result.score}てん</div>
          <div className="assign-stars">{starStr(result.stars)}</div>
          <p className="assign-feedback">{result.feedback}</p>
          {result.details && <p className="assign-details">{result.details}</p>}

          {audioUrl && (
            <button className="assign-btn-sub" onClick={handlePlay}>
              {playing ? '⏸ とめる' : '▶ きいてみる'}
            </button>
          )}

          {isHonban ? (
            <>
              <p className="assign-submitted">✅ せんせいに　ていしゅつしたよ！</p>
              <p className="assign-note">「きろく」にも　のこったよ</p>
            </>
          ) : (
            <div className="assign-result-actions">
              {renshyuLeft > 0 ? (
                <button className="assign-btn-primary" onClick={() => chooseMode('renshyu')}>
                  もういちど　れんしゅう（あと{renshyuLeft}かい）
                </button>
              ) : (
                <p className="assign-note">きょうの　れんしゅうは　おわり！</p>
              )}
              {honbanLeft > 0 && (
                <button className="assign-btn-sub" onClick={() => chooseMode('honban')}>
                  🎯 ほんばんに　すすむ
                </button>
              )}
            </div>
          )}

          {/* ここを知らないと、課題にも自分のきろくにも戻れなくなる */}
          <button className="assign-btn-sub" onClick={goMyPage}>
            🎒 生徒ページを　みる
          </button>
        </div>
      )}
    </div>
  );
}
